import { useRef } from 'react';
import { Check } from '@phosphor-icons/react';
import type { LogItem, LogStatus } from '../../content/site';
import { gsap, useGSAP, EASE, MOTION_OK } from '../../lib/gsap';
import { HALO } from './halo';
import { glue } from './glue';
import { reachTime } from './reach';

type LogContent = {
	title: string;
	statusLabels: Record<LogStatus, string>;
	items: LogItem[];
};

const DRAW = 1.5;

/** Small round marker: check (done), softly pulsing dot (doing), hollow circle (next). */
function Marker({ status }: { status: LogStatus }) {
	return (
		<span
			aria-hidden
			data-marker
			className="absolute left-0 top-0 flex size-[22px] items-center justify-center rounded-full"
		>
			{status === 'done' && (
				<span className="flex size-full items-center justify-center rounded-full border border-solar bg-forest-night text-solar">
					<Check size={12} weight="bold" />
				</span>
			)}
			{status === 'doing' && (
				<>
					<span className="absolute size-3 rounded-full bg-solar/60 motion-safe:animate-ping" />
					<span className="relative size-2.5 rounded-full bg-solar shadow-[0_0_10px_rgba(255,200,87,0.6)]" />
				</>
			)}
			{status === 'next' && <span className="size-full rounded-full border border-mineral/40 bg-forest-night" />}
		</span>
	);
}

/**
 * The journal of updates, hung under the "Aujourd'hui" horizon: milestones on a thin
 * vertical line that draws itself, each item appearing as the line reaches it.
 */
export function Log({ log }: { log: LogContent }) {
	const ref = useRef<HTMLDivElement>(null);

	useGSAP(
		() => {
			const wrap = ref.current;
			if (!wrap) return;
			const mm = gsap.matchMedia();

			mm.add(MOTION_OK, () => {
				const line = wrap.querySelector<HTMLElement>('[data-log-line]');
				if (!line) return;

				// Measure before any tween renders (from() hides the line immediately).
				const box = line.getBoundingClientRect();
				const entries = gsap.utils.toArray<HTMLElement>('[data-log-item]', wrap).map((el) => {
					const r = el.getBoundingClientRect();
					const fraction = box.height > 0 ? (r.top + 11 - box.top) / box.height : 0;
					return { el, at: reachTime(fraction) * DRAW };
				});

				const tl = gsap.timeline({ scrollTrigger: { trigger: wrap, start: 'top 82%' } });
				tl.from(line, { scaleY: 0, duration: DRAW, ease: 'power2.inOut' }, 0);
				entries.forEach(({ el, at }) => {
					const marker = el.querySelector('[data-marker]');
					const body = el.querySelector('[data-log-body]');
					if (marker) tl.from(marker, { scale: 0, duration: 0.6, ease: EASE }, at);
					if (body) tl.from(body, { y: 14, opacity: 0, duration: 0.9, ease: EASE }, at + 0.05);
				});
			});
		},
		{ scope: ref },
	);

	return (
		<div ref={ref} className="mt-10 md:mt-12">
			<h4 id="vision-log-title" className="font-mono text-[13px] font-medium uppercase tracking-[0.12em] text-dusk-muted">
				{log.title}
			</h4>
			<div className="relative mt-5">
				<span aria-hidden data-log-line className="absolute bottom-3 left-[10.5px] top-3 w-px origin-top bg-mineral/15" />
				<ul aria-labelledby="vision-log-title" className="grid gap-y-5">
					{log.items.map((item) => (
						<li key={item.text} data-log-item className="relative min-h-[22px] pl-9">
							<Marker status={item.status} />
							<div data-log-body>
								<span
									className={`block font-mono text-[12px] font-medium uppercase leading-[22px] tracking-[0.1em] ${
										item.status === 'next' ? 'text-dusk-muted' : 'text-solar'
									}`}
								>
									{log.statusLabels[item.status]}
								</span>
								<span className={`block text-[15px] leading-snug text-mineral/90 ${HALO}`}>{glue(item.text)}</span>
							</div>
						</li>
					))}
				</ul>
			</div>
		</div>
	);
}
